<template>
  <q-dialog v-model="visible" persistent maximized>
    <q-card class="modal-card" :class="{ 'modal-card-compact': compactView }">
      <div class="modal-header">
        <q-card-section class="header">
          <div class="header-content-wrapper">
            <div class="header-title">{{ postoName }}</div>
            <q-btn
              icon="close"
              flat
              round
              color="white"
              @click="onClose"
              class="header-close-btn"
              style="z-index: 1001;"
            />
          </div>
        </q-card-section>
      </div>

      <div class="modal-body">
        <!-- Loading geral da modal -->
        <div v-if="loading" class="modal-loading-overlay">
          <div class="loading-content">
            <q-spinner size="80px" color="primary" />
          </div>
        </div>

        <!-- Conteúdo da modal (só exibe quando não está carregando) -->
        <div v-else>
          <!-- Header com título e botões -->
          <div class="content-header">
          <div class="residuos-title">{{ this.$gettext('INDUSTRIAL_MAP_WASTES') }}</div>
          <div class="action-buttons">
            <q-btn
              :label="$gettext('INDUSTRIAL_MAP_BTN_SCHEDULES')"
              icon="event"
              class="btn-action"
              flat
              @click="openAgendamentosModal"
            />
            <q-btn
              :label="$gettext('INDUSTRIAL_MAP_BTN_HISTORY')"
              icon="history"
              class="btn-action"
              flat
              @click="openHistoricoModal"
            />
          </div>
        </div>

        <!-- Conteúdo em duas colunas (ou uma coluna em compactView) -->
        <div class="modal-content-wrapper" :class="{ 'compact-view': compactView }">
          <!-- Seção de resíduos (primeira coluna no modo normal) -->
          <div class="residuos-column" :class="{ 'compact-residuos': compactView }">
            <div class="q-mt-md">
              <q-expansion-item
                v-for="(group, index) in residueGroups"
                :key="index"
                class="coletas-realizadas-header q-mb-sm"
                header-class="text-white"
                expand-icon-class="text-white"
                :style="{ backgroundColor: group.color }"
                @show="onAccordionShow(group.id)"
                @hide="onAccordionHide(group.id)"
              >
                <template v-slot:header>
                  <div class="accordion-header-custom">
                    <span class="accordion-label">{{ group.label }}</span>
                    <div class="accordion-capacity-box">
                      <span class="capacity-label">Capacidade utilizada</span>
                      <span class="capacity-value">{{ group.capacidadeUtilizadaPercentual }}</span>
                    </div>
                  </div>
                </template>
                <q-card>
                  <q-card-section class="expanded-details">
                    <!-- Informações do acondicionamento -->
                    <div class="acondicionamento-info">
                      <div class="info-row">
                        <div class="info-item">
                          <span class="info-label">{{ $gettext('INDUSTRIAL_MAP_USED_CAPACITY') }}</span>
                          <span class="info-value">{{ group.capacidadeUtilizada }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">{{ $gettext('INDUSTRIAL_MAP_NEXT_COLLECTION') }}</span>
                          <span class="info-value">{{ group.proximaColeta }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">{{ $gettext('INDUSTRIAL_MAP_NEXT_ROUND') }}</span>
                          <span class="info-value">{{ group.proximaRonda }}</span>
                        </div>
                      </div>
                      <div class="info-row info-row-2cols">
                        <div class="info-item">
                          <span class="info-label">{{ $gettext('INDUSTRIAL_MAP_COLLECTIONS_DONE') }}</span>
                          <div class="info-value">
                            <div class="coletas-list">
                              <div v-for="(coleta, idx) in group.coletasRealizadasList" :key="idx">
                                {{ coleta }}
                              </div>
                              <div v-if="!group.coletasRealizadasList || group.coletasRealizadasList.length === 0">
                                {{ $gettext('INDUSTRIAL_MAP_NO_COLLECTION') }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="info-item">
                          <span class="info-label">{{ $gettext('INDUSTRIAL_MAP_ROUNDS_DONE') }}</span>
                          <span class="info-value">{{ group.rondasFeitasDetalhes }}</span>
                        </div>
                      </div>
                    </div>
                  </q-card-section>
                </q-card>
              </q-expansion-item>
            </div>
          </div>

          <!-- Seção de informações (segunda coluna no modo normal, primeira no compactView) -->
          <div class="informacoes-column" :class="{ 'compact-info': compactView }">
            <div class="informacoes-header-row">
              <div class="informacoes-header-row-title">{{ this.$gettext('INDUSTRIAL_MAP_LAST_UPDATED') }} {{ formattedLastUpdateTime }}</div>
              <div class="stats-container">
                <div class="stat-block">
                  <div class="text-h6 q-mt-sm">{{ this.$gettext('INDUSTRIAL_MAP_CURRENT_VOLUME') }}</div>
                  <VolumeIcon />
                  <div class="text-h4 text-weight-bold text-modal-body">{{ (advancedPostoDetails && advancedPostoDetails.volumeAtual) || '0%' }}</div>
                </div>
                <div class="stat-block">
                  <div class="text-h6 q-mt-sm">{{ this.$gettext('INDUSTRIAL_MAP_COLLECTIONS_DONE') }}</div>
                  <TruckIcon />
                  <div class="text-h4 text-weight-bold text-modal-body">{{ (advancedPostoDetails && advancedPostoDetails.coletasRealizadas) || 0 }}</div>
                </div>
                <div class="stat-block">
                  <div class="text-h6 q-mt-sm">{{ this.$gettext('INDUSTRIAL_MAP_ROUNDS_DONE') }}</div>
                  <RondasIcon />
                  <div class="text-h4 text-weight-bold text-modal-body">{{ (advancedPostoDetails && advancedPostoDetails.rondasFeitas) || 0 }}</div>
                </div>
              </div>
            </div>
            <!-- Diagrama do posto ou imagem (escondido em compactView) -->
            <div v-if="!compactView" class="diagram-container">
              <div v-if="loadingImage" class="loading-image">
                <q-spinner size="50px" color="primary" />
                <div class="loading-text">Carregando imagem...</div>
              </div>
              <div v-else-if="postoImage" class="posto-image-container">
                <img :src="postoImage" alt="Imagem do Posto" class="posto-image" />
              </div>
              <div v-else class="no-image-placeholder">
                <q-icon name="photo" size="64px" color="grey-5" />
                <div class="no-image-text">Imagem não disponível</div>
              </div>
            </div>
          </div>
        </div>
        </div> <!-- Fim do conteúdo da modal -->
      </div>

            <div class="modal-footer">
        <q-card-actions align="center" class="footer">
          <q-btn
            :label="$gettext('INDUSTRIAL_MAP_BTN_CLOSE')"
            color="grey-7"
            class="btn-fechar"
            @click="onClose"
            style="z-index: 1001;"
          />
        </q-card-actions>
      </div>
    </q-card>

    <!-- Modal de Agendamentos -->
    <ModalAgendamentos
      v-model="showAgendamentosModal"
      :posto="data"
      @close="showAgendamentosModal = false"
    />

    <!-- Modal de Histórico -->
    <ModalFiltrosDinamicos
      v-if="modalHistorico.visible"
      key="modal-historico-pa"
      v-model="modalHistorico.visible"
      :nm-interface="modalHistorico.nmInterface"
      :fields="modalHistorico.tableFields"
      :forms="modalHistorico.forms"
      :api-mode="true"
      :extra-params="modalHistorico.extraParams"
      :buttons="modalHistorico.buttons"
      :relacionamentos="modalHistorico.relacionamentos"
      :selection-force="true"
      :rows-per-page-initial="10"
      :is-paginated="true"
      :row-buttons="false"
      noneselect
      :title="$gettext('INDUSTRIAL_MAP_OUTPOST_HISTORY')"
      show-day-filter
      :day-filter-label="$gettext('INDUSTRIAL_MAP_OUTPOST_HISTORY')"
      @day-filter-change="onHistoricoDayFilterChange"
      @close="closeHistoricoModal"
      @search-completed="onHistoricoSearchCompleted"
      @search-error="onHistoricoSearchError"
    >
      <!-- Body slot com expansão nativa do QTable -->
      <template v-slot:body="{ props }">
        <q-tr :props="props" :class="{ 'historico-row-expanded': props.expand }">
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <template v-if="col.name === 'showhide'">
              <q-btn
                flat
                round
                color="primary"
                class="q-mr-sm"
                :icon="props.expand ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                @click="onToggleExpandRow(props)"
              />
            </template>
            <span v-else>{{ col.value }}</span>
          </q-td>
        </q-tr>
        <q-tr v-show="props.expand" :props="props" class="historico-expand-row">
          <q-td colspan="100%" class="historico-expand-td">
            <!-- Loading -->
            <div v-if="isRowDetailLoading(props.row)" class="historico-expand-loading">
              <q-spinner size="24px" color="primary" class="q-mr-sm" />
              <span>Carregando detalhes...</span>
            </div>
            <!-- Erro -->
            <div v-else-if="getRowDetailError(props.row)" class="historico-expand-error">
              Erro ao carregar detalhes. <a href="#" @click.prevent="fetchRowDetail(props.row)">Tentar novamente</a>
            </div>
            <!-- Dados carregados -->
            <div v-else class="historico-expand-content">
              <template v-if="hasIndustrialTaskItems(props.row)">
                <div
                  v-for="(item, idx) in getIndustrialTaskItems(props.row)"
                  :key="item.id || idx"
                  class="historico-expand-info-row"
                >
                  <div class="historico-expand-info-col">
                    <span class="historico-expand-info-label">Produto</span>
                    <span class="historico-expand-info-value">{{ item.product || '-' }}</span>
                  </div>
                  <div class="historico-expand-info-col">
                    <span class="historico-expand-info-label">{{ $gettext('INDUSTRIAL_MAP_OBSERVATION') }}</span>
                    <span class="historico-expand-info-value">{{ item.observations || '-' }}</span>
                  </div>
                </div>
              </template>
              <div v-else class="historico-expand-empty">
                Nenhum acondicionamento registrado para esta atividade.
              </div>
            </div>
          </q-td>
        </q-tr>
      </template>
    </ModalFiltrosDinamicos>
  </q-dialog>
</template>

<script>
import VolumeIcon from 'src/components/industrialMap/icons/VolumeIcon.vue'
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import moment from 'moment'
import { IndustrialMapRepository } from 'src/modules/industry/industrial-map/IndustrialMapRepository.js'
import TruckIcon from 'src/components/industrialMap/icons/TruckIcon.vue'
import RondasIcon from 'src/components/industrialMap/icons/RondasIcon.vue'
import ModalAgendamentos from './ModalAgendamentos.vue'
import ModalFiltrosDinamicos from './ModalFiltrosDinamicos.vue'

@Component({
  name: 'ModalDetalhesPostoAvancado',
  components: {
    VolumeIcon,
    TruckIcon,
    RondasIcon,
    ModalAgendamentos,
    ModalFiltrosDinamicos
  }
})
class ModalDetalhesPostoAvancado extends Vue {
  @Prop({ type: Boolean, default: false })
  value // Controls modal visibility

  @Prop({ type: Object, default: null })
  data // Posto data object with id

  @Prop({ type: Boolean, default: false })
  compactView // Controls if should hide the image/diagram section (true = hide image)

  loading = false
  hasError = false
  errorMessage = ''
  isClosing = false // Flag para prevenir múltiplas chamadas do onClose
  advancedPostoDetails = null // This will hold the fetched data for advanced posto
  showAgendamentosModal = false
  lastUpdateTime = null // Horário da última atualização dos dados
  postoImage = null // Imagem do posto de coleta
  loadingImage = false // Estado de carregamento da imagem

  // State for histórico modal
  modalHistorico = {
    visible: false,
    nmInterface: '',
    tableFields: [],
    forms: [],
    extraParams: {},
    buttons: [],
    headerData: null
  }

  // Cache de dados de detalhe das linhas expandidas (taskId -> { loading, items, error })
  expandedRowsData = {}

  get visible () {
    return this.value
  }

  set visible (val) {
    this.$emit('input', val)
  }

  get postoName () {
    return (this.advancedPostoDetails && this.advancedPostoDetails.name) || 'Posto Avançado'
  }

  get formattedLastUpdateTime () {
    if (!this.lastUpdateTime) {
      return '00:00'
    }
    return moment(this.lastUpdateTime).format('DD/MM/YYYY HH:mm')
  }

  get residueGroups () {
    // This computed property will transform advancedPostoDetails into the format needed for v-for
    // It processes the 'acondicionamentos' from the service response
    if (!this.advancedPostoDetails || !this.advancedPostoDetails.acondicionamentos) {
      return []
    }
    return this.advancedPostoDetails.acondicionamentos.map(acondicionamento => ({
      label: `${this.capitalizeText(acondicionamento.industrialPacking?.name)} / ${this.capitalizeText(acondicionamento.residue.name)}`,
      color: this.getResidueColor(acondicionamento.residue.name),
      capacidadeUtilizada: `${acondicionamento.wastePackingUsedPercentage || 0}% (${acondicionamento.wastePackingKgQuantity || 0} kg)`,
      capacidadeUtilizadaPercentual: `${acondicionamento.wastePackingUsedPercentage || 0}%`,
      proximaColeta: acondicionamento.proximaColeta,
      proximaRonda: acondicionamento.proximaRonda,
      coletasRealizadasDetalhes: this.formatColetasRealizadas(acondicionamento.coletasRealizadas, acondicionamento.coletasDoDia),
      coletasRealizadasList: this.formatColetasRealizadasList(acondicionamento.coletasDoDia),
      rondasFeitasDetalhes: 'Não programada' // Placeholder, as rondas are not detailed in current data
    }))
  }

  formatColetasRealizadas (total, coletas) {
    if (!coletas || coletas.length === 0) {
      return `${total || 0}ª - 00:00`
    }
    return coletas.map((c, idx) => `${idx + 1}ª - ${this.formatTime(c.scheduledDate)}`).join('\n')
  }

  formatColetasRealizadasList (coletas) {
    if (!coletas || coletas.length === 0) {
      return []
    }
    return coletas.map((c, idx) => `${idx + 1}ª - ${c.horario}`)
  }

  formatRondasFeitas (total) {
    return 'Não programada'
  }

  formatDate (date) {
    return moment(date).format('DD/MM/YYYY HH:mm')
  }

  formatTime (dateTime) {
    return moment(dateTime).format('HH:mm')
  }

  // Novos métodos para processar dados dos acondicionamentos
  calculateVolumeAtual (packings) {
    if (!packings || packings.length === 0) {
      return '0%'
    }

    // Calcular a média das porcentagens de cada acondicionamento
    // Cada acondicionamento contribui igualmente para o volume total do posto
    const totalPercentage = packings.reduce((sum, packing) => {
      // Usar a porcentagem da API ou calcular se não estiver disponível
      let percentage = packing.wastePackingUsedPercentage

      if (percentage === undefined || percentage === null) {
        const capacity = packing.wastePackingCapacity || packing.capacity || 0
        const usedKg = packing.wastePackingKgQuantity || 0
        percentage = capacity > 0 ? (usedKg / capacity) * 100 : 0
      }

      return sum + percentage
    }, 0)

    // Calcular a média: soma das porcentagens / quantidade de acondicionamentos
    const averagePercentage = Math.round(totalPercentage / packings.length)

    return `${averagePercentage}%`
  }

  calculateOccupancyRate (packing) {
    // Simular taxa de ocupação baseada no número de coletas vs agendamentos
    const collected = packing.collected?.length || 0
    const scheduled = packing.scheduled?.length || 0
    const total = collected + scheduled

    if (total === 0) return 0

    // Se há mais coletas realizadas que agendadas, taxa menor
    // Se há mais agendamentos, taxa maior (acúmulo)
    const occupancyBase = Math.min(85, (scheduled / Math.max(1, collected)) * 30)
    return Math.round(occupancyBase + Math.random() * 15) // Adicionar variação
  }

  getProximaColeta (scheduled) {
    if (!scheduled || scheduled.length === 0) return 'Não agendada'

    // Buscar próximo agendamento por data
    const proximoAgendamento = scheduled
      .filter(s => moment(s.scheduledDate).isAfter(moment()))
      .sort((a, b) => moment(a.scheduledDate).diff(moment(b.scheduledDate)))[0]

    if (proximoAgendamento) {
      return this.formatDate(proximoAgendamento.scheduledDate)
    }

    // Se não há agendamentos futuros, retornar o mais recente
    const ultimoAgendamento = scheduled
      .sort((a, b) => moment(b.scheduledDate).diff(moment(a.scheduledDate)))[0]

    return ultimoAgendamento ? this.formatDate(ultimoAgendamento.scheduledDate) : 'Não agendada'
  }

  getProximaRonda (scheduled) {
    if (!scheduled || scheduled.length === 0) return 'Não programada'

    // Simular horário de ronda baseado nos agendamentos
    const proximoAgendamento = scheduled
      .filter(s => moment(s.scheduledDate).isAfter(moment()))
      .sort((a, b) => moment(a.scheduledDate).diff(moment(b.scheduledDate)))[0]

    if (proximoAgendamento) {
      const hora = moment(proximoAgendamento.scheduledDate).hour()
      const periodo = hora < 12 ? 'Manhã' : hora < 18 ? 'Tarde' : 'Noite'
      const turno = hora < 6 ? 1 : hora < 12 ? 2 : hora < 18 ? 3 : 4
      return `${periodo} ${turno} (${this.formatTime(proximoAgendamento.scheduledDate)})`
    }

    return 'Não programada'
  }

  getColetasDoDia (collected) {
    if (!collected || collected.length === 0) return []

    const hoje = moment().startOf('day')
    const coletasHoje = collected.filter(c =>
      moment(c.scheduledDate).isSame(hoje, 'day')
    )

    return coletasHoje.map(c => ({
      id: c.id,
      horario: this.formatTime(c.scheduledDate),
      status: c.taskStatusType
    }))
  }

  onClose () {
    // Prevenir multiple calls usando um flag
    if (this.isClosing) {
      return
    }
    this.isClosing = true

    // Limpar loading states primeiro
    this.loading = false
    this.loadingImage = false

    // Limpar URL do blob para evitar vazamento de memória
    if (this.postoImage) {
      URL.revokeObjectURL(this.postoImage)
      this.postoImage = null
    }

    // Resetar todos os dados para garantir limpeza completa
    this.advancedPostoDetails = null
    this.lastUpdateTime = null
    this.hasError = false
    this.errorMessage = ''

    // Fechar modal primeiro via prop
    this.$emit('input', false)

    // Em seguida emitir evento de close
    this.$emit('close')

    // Reset flag after a short delay
    this.$nextTick(() => {
      this.isClosing = false
    })
  }

  @Watch('visible')
  async onModalOpen (isVisible) {
    if (isVisible && this.data?.id) {
      // Resetar dados do posto anterior para evitar exibição de informações antigas
      this.advancedPostoDetails = null
      this.postoImage = null

      // Sempre atualizar o horário quando o modal é aberto
      await this.loadAdvancedPostoDetails()
    }
  }

  async loadAdvancedPostoDetails () {
    try {
      this.loading = true

      // Carregar dados do posto e imagem em paralelo
      const [response, imageBlob] = await Promise.allSettled([
        IndustrialMapRepository.mountOutpostDetail(this.data.id, this.data.filterDate),
        IndustrialMapRepository.downloadImagesOutposts(this.data.id)
      ])

      // Processar resposta dos dados do posto
      if (response.status === 'fulfilled' && response.value && response.value.detalhes && response.value.detalhes.dadosForm) {
        const dadosForm = response.value.detalhes.dadosForm

        // Calcular estatísticas totais
        // Contar coletas distintas por industrialTask (ID da tarefa).
        // Uma única coleta pode coletar múltiplos acondicionamentos simultaneamente,
        // portanto somamos tarefas únicas e não itens de acondicionamento.
        const allCollectedTaskIds = new Set();
        (dadosForm.industrialPackingCollectionPoints || []).forEach(packing => {
          (packing.collected || []).forEach(c => {
            // Agrupar por industrialTask (ID da tarefa) para evitar contagem duplicada.
            // Fallback: se não houver industrialTask, agrupar por scheduledDate (mesma hora = mesma coleta).
            const key = c.industrialTask || c.scheduledDate
            if (key) allCollectedTaskIds.add(String(key))
          })
        })
        const totalColetas = allCollectedTaskIds.size

        const totalAgendamentos = (dadosForm.industrialPackingCollectionPoints || [])
          .reduce((total, packing) => total + (packing.scheduled?.length || 0), 0)

        // Mapear dados da API para o formato esperado pelo modal
        this.advancedPostoDetails = {
          id: dadosForm.id,
          name: dadosForm.name,
          volumeAtual: this.calculateVolumeAtual(dadosForm.industrialPackingCollectionPoints || []),
          coletasRealizadas: totalColetas,
          rondasFeitas: 0, // Usando agendamentos como rondas
          acondicionamentos: (dadosForm.industrialPackingCollectionPoints || []).map(packing => ({
            id: packing.id,
            industrialPacking: {
              name: packing.code,
              capacity: packing.capacity
            },
            residue: {
              code: packing.productCode,
              name: packing.productName
            },
            color: this.getResidueColor(packing.productName),
            occupancyRate: 'Não Informado',
            // Novos campos da API
            wastePackingCapacity: packing.wastePackingCapacity || packing.capacity,
            wastePackingUsedPercentage: packing.wastePackingUsedPercentage || 0,
            wastePackingKgQuantity: packing.wastePackingKgQuantity || 0,
            proximaColeta: this.getProximaColeta(packing.scheduled || []),
            proximaRonda: this.getProximaRonda([]),
            coletasRealizadas: packing.collected?.length || 0,
            coletasDoDia: this.getColetasDoDia(packing.collected || []),
            rondasRealizadas: 0,
            // Dados originais para referência
            collected: packing.collected || [],
            scheduled: packing.scheduled || []
          }))
        }

        // Usar a data mais recente entre updatedAt dos acondicionamentos e scheduledDate das coletas realizadas
        // O updatedAt do acondicionamento só reflete mudanças na entidade (capacidade/percentual/peso),
        // mas NÃO é atualizado quando coletas ou rondas são realizadas.
        // Por isso, também consideramos as datas das coletas realizadas (collected) como fonte de atualização.
        const packings = dadosForm.industrialPackingCollectionPoints || []

        const allDates = []

        // 1. Datas de updatedAt dos acondicionamentos
        packings.forEach(p => {
          if (p.updatedAt) {
            allDates.push(moment(p.updatedAt, 'YYYY-MM-DD HH:mm:ss'))
          }
        })

        // 2. Datas das coletas realizadas (scheduledDate dos collected)
        packings.forEach(p => {
          const collected = p.collected || []
          collected.forEach(c => {
            if (c.scheduledDate) {
              allDates.push(moment(c.scheduledDate))
            }
          })
        })

        // Usar a data mais recente dentre todas as fontes
        const validDates = allDates.filter(d => d.isValid())
        const mostRecentDate = validDates.length > 0
          ? validDates.reduce((max, d) => d.isAfter(max) ? d : max)
          : null
        this.lastUpdateTime = mostRecentDate || moment()

        this.hasError = false
        this.errorMessage = ''

        // Processar imagem do posto
        if (imageBlob.status === 'fulfilled' && imageBlob.value) {
          this.postoImage = URL.createObjectURL(imageBlob.value)
        } else {
          console.warn('[ModalDetalhesPostoAvancado] Erro ao carregar imagem do posto:', imageBlob.reason)
          this.postoImage = null
        }
      } else {
        this.hasError = true
        this.errorMessage = 'Detalhes do posto avançado não encontrados.'
        console.error('Erro ao carregar detalhes do posto avançado: Dados não encontrados para o ID', this.data.id)
        this.$q.notify({
          type: 'negative',
          message: this.$gettext('INDUSTRIAL_MAP_ERROR_LOAD_OUTPOST_DETAILS')
        })
      }
    } catch (error) {
      this.hasError = true
      this.errorMessage = 'Não foi possível exibir o registro. Erro: ' + error.message
      console.error('Erro ao carregar detalhes do posto avançado:', error)
      this.$q.notify({
        type: 'negative',
        message: this.$gettext('INDUSTRIAL_MAP_ERROR_LOAD_OUTPOST_DETAILS')
      })
    } finally {
      this.loading = false
    }
  }

  getPackingColor (code) {
    // Mapear cores baseado no tipo de acondicionamento
    const colorMap = {
      P000366: '#FF6B6B',
      Cacamba: '#4ECDC4',
      Granel: '#95E1D3',
      Saco: '#F38181',
      Gaiola: '#AA96DA'
    }
    return colorMap[code] || '#666'
  }

  getResidueColor (residueName) {
    // Mapear cores baseado no tipo de resíduo
    if (!residueName) return '#7F7F7F'

    const residueLower = residueName.toLowerCase()

    // Resíduos Perigosos
    if (residueLower.includes('perigoso') || residueLower.includes('componentes eletronicos')) {
      return '#FFA000'
    }

    // Estojo fio dental, fio encerrado, cera
    if (residueLower.includes('estojo') || residueLower.includes('fio dental') ||
        residueLower.includes('fio encerrado') || residueLower.includes('cera')) {
      return '#01ADC6'
    }

    // Plástico
    if (residueLower.includes('plastico') || residueLower.includes('plástico')) {
      return '#FF0000'
    }

    // Papel, Papelão, papel Toalha
    if (residueLower.includes('papel') || residueLower.includes('papelao') ||
        residueLower.includes('papelão') || residueLower.includes('toalha')) {
      return '#0000FF'
    }

    // Vidro
    if (residueLower.includes('vidro')) {
      return '#199B69'
    }

    // Madeira
    if (residueLower.includes('madeira')) {
      return '#000000'
    }

    // Lixo Comum (padrão)
    return '#7F7F7F'
  }

  capitalizeText (text) {
    if (!text) return ''
    return text.toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  openAgendamentosModal () {
    this.showAgendamentosModal = true
  }

  openHistoricoModal () {
    this.openHistoricoFiltrosModal(this.data.id)
  }

  async openHistoricoFiltrosModal (collectionPoint) {
    try {
      // Buscar dados do header para configurar o formulário usando findHistoryOutposts
      const response = await IndustrialMapRepository.findHistoryOutposts(collectionPoint)

      if (response && response.header) {
        // Processar a estrutura completa retornada pelo backend
        const headerData = response.header[0] // Primeiro item do array header

        let forms = []
        let datatables = []

        // Verificar se o headerData tem inputs diretamente
        if (headerData.inputs && Array.isArray(headerData.inputs)) {
          // Criar um form com os inputs do header (excluindo DATATABLE)
          const searchInputs = headerData.inputs.filter(input => input.type !== 'DATATABLE')
          forms = [{
            name: headerData.name,
            inputs: searchInputs
          }]

          // Extrair configuração da grid dos inputs DATATABLE
          const datatableInputs = headerData.inputs.filter(input => input.type === 'DATATABLE')
          if (datatableInputs.length > 0) {
            const datatableInput = datatableInputs[0]

            // Criar datatable a partir do input DATATABLE
            if (datatableInput.columns && Array.isArray(datatableInput.columns)) {
              datatables = [{
                nmInterface: datatableInput.nmInterface || headerData.nmInterface,
                columns: datatableInput.columns,
                buttons: datatableInput.buttons || [],
                name: datatableInput.name
              }]
            }
          }
        }

        // Verificar se o headerData tem datatables diretamente
        if (headerData.datatables && Array.isArray(headerData.datatables)) {
          datatables = headerData.datatables
        }

        // Se não encontrou nos inputs diretos, tentar nos forms do header
        if (forms.length === 0 && headerData.forms && Array.isArray(headerData.forms)) {
          forms = headerData.forms
        }

        // Se ainda não encontrou, tentar nos detalhes
        if (forms.length === 0 && response.detalhes && response.detalhes.dadosForm) {
          forms = response.detalhes.dadosForm.forms || []
        }

        if (datatables.length === 0 && response.detalhes && response.detalhes.dadosForm) {
          datatables = response.detalhes.dadosForm.datatables || []
        }

        const firstDataTable = datatables[0] || {}

        // Configurar campos da tabela baseados nas colunas do datatable
        let tableFields = []
        if (firstDataTable.columns && Array.isArray(firstDataTable.columns)) {
          // Separar colunas de ação (botões) das colunas de dados
          const actionColumns = firstDataTable.columns.filter(col =>
            col.type === 'ACTION' ||
            col.name === 'AÇÀO' ||
            col.name === 'ACTIONS' ||
            col.field === 'actions' ||
            (col.buttons && col.buttons.length > 0)
          )

          const dataColumns = firstDataTable.columns.filter(col =>
            !(col.type === 'ACTION' ||
              col.name === 'AÇÀO' ||
              col.name === 'ACTIONS' ||
              col.field === 'actions' ||
              (col.buttons && col.buttons.length > 0))
          )

          // Definir ordem preferida para as colunas de histórico
          const preferredOrder = ['date', 'outpostName', 'actionType', 'description', 'responsible', 'status']

          const sortedColumns = dataColumns.sort((a, b) => {
            const indexA = preferredOrder.indexOf(a.name) !== -1 ? preferredOrder.indexOf(a.name) : 999
            const indexB = preferredOrder.indexOf(b.name) !== -1 ? preferredOrder.indexOf(b.name) : 999
            return indexA - indexB
          })

          // Criar campos para todas as colunas na ordem correta
          tableFields = sortedColumns.map((col, index) => {
            // Ajustar larguras das colunas para otimizar espaço
            let style = 'width: auto; max-width: 200px; overflow: hidden; text-overflow: ellipsis;'

            if (col.name === 'date' || col.field === 'date') {
              style = 'width: 120px; max-width: 120px;'
            } else if (col.name === 'outpostName' || col.field === 'outpostName') {
              style = 'width: 200px; max-width: 200px;'
            } else if (col.name === 'actionType' || col.field === 'actionType') {
              style = 'width: 150px; max-width: 150px;'
            } else if (col.name === 'description' || col.field === 'description') {
              style = 'width: 250px; max-width: 250px;'
            } else if (col.name === 'responsible' || col.field === 'responsible') {
              style = 'width: 150px; max-width: 150px;'
            } else if (col.name === 'status' || col.field === 'status') {
              style = 'width: 100px; max-width: 100px;'
            }

            return {
              name: col.name || col.model,
              label: col.label,
              field: col.name || col.field || col.model,
              align: 'center',
              sortable: true,
              type: col.type,
              dataType: col.dataType,
              render: col.render,
              visibility: col.visibility || 'show',
              style: style,
              order: col.order || index
            }
          })

          // Adicionar campo showhide para o botão de expansão da linha
          tableFields.push({
            name: 'showhide',
            label: '',
            field: 'showhide',
            align: 'center',
            sortable: false,
            style: 'width: 50px; max-width: 50px;'
          })
        }

        // Configurar dados do modal - usar nmInterface do datatable se disponível
        const modalNmInterface = firstDataTable.nmInterface || headerData.nmInterface || 'outpostHistory.pesquisar'
        this.modalHistorico.nmInterface = modalNmInterface
        this.modalHistorico.tableFields = tableFields
        this.modalHistorico.forms = forms
        this.modalHistorico.buttons = firstDataTable.buttons || []
        this.modalHistorico.headerData = headerData
        this.modalHistorico.relacionamentos = response.relacionamentos.relacionamentos || {}
        this.modalHistorico.extraParams = {
          collectionPoint: collectionPoint,
          interface: modalNmInterface,
          grid: 'findIndustrialTasksByCollectionPoint'
        }
      }

      this.modalHistorico.visible = true
    } catch (error) {
      console.error('[ModalDetalhesPostoAvancado] Erro ao carregar dados do histórico:', error)
      this.$q.notify({
        type: 'negative',
        message: this.$gettext('INDUSTRIAL_MAP_ERROR_LOAD_HISTORY')
      })
    }
  }

  // Handlers para modal de histórico
  onHistoricoSearchCompleted (response) {
    this.$q.notify({
      type: 'positive',
      message: this.$gettext('INDUSTRIAL_MAP_SUCCESS_LOAD_HISTORY')
    })
  }

  onHistoricoSearchError (error) {
    console.error('[ModalDetalhesPostoAvancado] Erro na pesquisa de histórico:', error)
    this.$q.notify({
      type: 'negative',
      message: this.$gettext('INDUSTRIAL_MAP_ERROR_SEARCH_HISTORY')
    })
  }

  closeHistoricoModal () {
    this.modalHistorico.visible = false
    this.modalHistorico.tableFields = []
    this.modalHistorico.forms = []
    this.modalHistorico.extraParams = {}
    this.modalHistorico.buttons = []
    this.modalHistorico.headerData = null
    this.expandedRowsData = {}
  }

  onHistoricoDayFilterChange (days) {
    this.$emit('historico-day-filter-change', days)
  }

  /**
   * Alterna expansão da linha e busca detalhes sob demanda
   */
  onToggleExpandRow (props) {
    props.expand = !props.expand
    if (props.expand) {
      const row = props.row
      this.fetchRowDetail(row)
    }
  }

  /**
   * Busca detalhes da tarefa industrial via composicaoDetalhar
   */
  async fetchRowDetail (row) {
    const taskId = row.id
    if (!taskId) return

    // Se já foi carregado com sucesso, não buscar novamente
    if (this.expandedRowsData[taskId] && this.expandedRowsData[taskId].items) return

    // Marcar como loading usando $set para reatividade
    this.$set(this.expandedRowsData, taskId, { loading: true, items: null, error: false })

    try {
      const response = await IndustrialMapRepository.findIndustrialTaskDetail(taskId)
      const data = response?.dadosForm || response || {}
      const items = data.industrialTaskItems || data.industrialTaskItemList || []
      this.$set(this.expandedRowsData, taskId, { loading: false, items, error: false })
    } catch (error) {
      console.error(`[ModalDetalhesPostoAvancado] Erro ao buscar detalhes da tarefa ${taskId}:`, error)
      this.$set(this.expandedRowsData, taskId, { loading: false, items: null, error: true })
    }
  }

  /**
   * Verifica se o detalhe da linha está carregando
   */
  isRowDetailLoading (row) {
    const data = this.expandedRowsData[row?.id]
    return data ? data.loading : false
  }

  /**
   * Retorna erro de carregamento da linha
   */
  getRowDetailError (row) {
    const data = this.expandedRowsData[row?.id]
    return data ? data.error : false
  }

  /**
   * Retorna os itens de acondicionamento da linha expandida (do cache)
   */
  getIndustrialTaskItems (row) {
    if (!row) return []
    const cached = this.expandedRowsData[row.id]
    if (cached && cached.items) return cached.items
    return []
  }

  /**
   * Verifica se a linha possui itens de acondicionamento
   */
  hasIndustrialTaskItems (row) {
    return this.getIndustrialTaskItems(row).length > 0
  }

  /**
   * Retorna texto resumo dos resíduos coletados
   */
  getCollectedResiduesText (row) {
    const items = this.getIndustrialTaskItems(row)
    if (items.length === 0) return '-'
    const products = items.map(item => item.product).filter(Boolean)
    return products.length > 0 ? products.join(', ') : '-'
  }

  /**
   * Retorna observações concatenadas dos itens
   */
  getObservations (row) {
    const items = this.getIndustrialTaskItems(row)
    const obs = items.map(item => item.observations).filter(Boolean)
    return obs.length > 0 ? obs.join('; ') : ''
  }
}

export default ModalDetalhesPostoAvancado
</script>

<style lang="stylus" scoped>
.modal-card
  width: 1857px
  max-width: 1857px
  height: 900px
  max-height: 900px
  border-radius: 10px
  padding-top: 16px
  background: white
  display: flex
  flex-direction: column
  gap: 20px
  box-shadow: 0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px rgba(0,0,0,0.14), 0 1px 10px rgba(0,0,0,0.12)

// Modal em modo compacto (menor largura)
.modal-card-compact
  width: 909px
  max-width: 909px
  height: 900px
  max-height: 900px

.modal-header
  flex-shrink: 0

.header
  height: 50px
  padding: 8px 20px
  border-radius: 4px
  margin:0px 10px
  color: white !important
  flex-shrink: 0
  background-color: #1E4971

.header-content-wrapper
  height: 34px
  gap: 20px
  display: flex
  align-items: center
  justify-content: space-between

.header-title
  height: 34px
  font-family: Roboto
  font-weight: 700
  font-style: Bold
  font-size: 28px
  line-height: 34px

.header-close-btn
  flex-shrink: 0
  cursor: pointer
  margin: 0

.modal-body
  flex-grow: 1
  overflow-y: auto
  padding: 0 16px
  display: flex
  flex-direction: column
  position: relative

.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(255, 255, 255, 0.95)
  display: flex
  justify-content: center
  align-items: center
  z-index: 1000

.loading-content
  display: flex
  flex-direction: column
  align-items: center
  gap: 24px

.loading-message
  font-size: 18px
  font-weight: 500
  color: #333
  text-align: center

.content-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 0 16px
  margin-bottom: 8px

.modal-content-wrapper
  display: flex
  flex: 1

// Modo compact view: layout em coluna única
.modal-content-wrapper.compact-view
  flex-direction: column

  .informacoes-column
    width: 100%
    height: auto
    margin-bottom: 16px
    order: 1 // Informações aparecem primeiro

  .residuos-column
    width: 100%
    height: auto
    order: 2 // Resíduos aparecem depois
    padding-right: 0

.residuos-column
  width: 916px
  height: 720px;
  display: flex
  flex-direction: column
  gap: 16px
  padding-right: 16px // Add padding to separate from content

  .q-expansion-item
    /deep/.q-item
      min-height: 38px!important

.accordion-header-custom
  display: flex
  justify-content: space-between
  align-items: center
  width: 100%
  padding-right: 12px

.accordion-label
  flex: 1
  font-size: 14px
  font-weight: 500

.accordion-capacity-box
  display: flex
  align-items: center
  gap: 8px
  padding: 4px 12px
  background: rgba(255, 255, 255, 0.25)
  border-radius: 4px
  white-space: nowrap

.capacity-label
  font-size: 12px
  font-weight: 400
  opacity: 0.95

.capacity-value
  font-size: 13px
  font-weight: 600

.residuos-title
  color: #707070
  margin: 0
  font-family: Roboto
  font-size: 20px
  font-style: normal
  font-weight: 500
  line-height: normal
  position: relative
  padding-bottom: 5px

  &::after
    content: ''
    position: absolute
    bottom: 0
    left: 0
    width: 5.13rem
    height: 3px
    background-color: #D18E4B

.residue-list
  display: flex
  flex-direction: column
  gap: 8px

.residue-item
  padding: 12px 16px
  border-radius: 8px
  color: white
  font-weight: 500
  cursor: pointer
  transition: background-color 0.3s ease
  opacity: 1;

.residue-item:hover
  opacity: 0.9

.informacoes-column
  width: 873px;
  height: 720px;
  angle: 0 deg;
  opacity: 1;
  border-radius: 8px;
  border-width: 1px;
  border: 1px solid #E0E0E0

.informacoes-header-row
  width: 873;
  height: 245;
  angle: 0 deg;
  opacity: 1;
  border-radius: 8px;
  padding: 16px;
  gap: 16px;

.informacoes-header-row-title
  font-size: 18px
  font-weight: 700
  color: #333

.action-buttons
  display: flex
  gap: 12px

.btn-action
  width: 186px
  height: 36px
  min-width: 160px
  font-size: 12px
  font-weight: 600
  text-transform: uppercase
  border-radius: 4px
  gap: 10px
  background: #0E88E4
  border: 1px solid #FFFFFF
  box-shadow: 1px 1px 3px 1px rgba(0, 0, 0, 0.16)
  color: white

.stats-container
  display: flex
  justify-content: space-around
  gap: 16px
  background: #FCFCFC
  padding: 16px
  border-radius: 8px
  border: 1px solid #E0E0E0

.stat-block
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  flex: 1

.stat-block .text-h6
  margin-bottom: 8px

.stat-block .text-h4
  margin-top: 8px

.text-modal-body
  color: #31CCCB

.diagram-container
  flex-grow: 1
  display: flex
  justify-content: center
  align-items: center
  border-radius: 8px
  overflow: hidden

.loading-image
  display: flex
  flex-direction: column
  align-items: center
  gap: 16px
  color: #666

.loading-text
  font-size: 14px
  color: #666

.posto-image-container
  width: 100%
  height: 100%
  display: flex
  justify-content: center
  align-items: center

.posto-image
  max-width: 100%
  max-height: 100%
  width: auto
  height: auto
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  object-fit: contain

.no-image-placeholder
  flex-grow: 1
  background: #f0f0f0
  border-radius: 8px
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  color: #888
  width: 100%
  height: 100%
  gap: 16px

.no-image-text
  font-size: 16px
  color: #888
  font-style: italic

.modal-footer
  flex-shrink: 0

.footer
  height: 60px
  padding: 12px 0
  background: #FFFFFF
  display: flex
  justify-content: center
  align-items: center
  flex-shrink: 0

.btn-fechar
  width: 120px

.expanded-content
  gap: 20px;
  opacity: 1;
  padding: 20px;
  border-width: 1px;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  box-shadow: 1px 1px 3px 1px #00000029;

.expanded-details
  padding: 16px
  background: #FFFFFF

.acondicionamento-info
  display: flex
  flex-direction: column
  gap: 12px

.info-row
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 8px

.info-row-2cols
  grid-template-columns: repeat(2, 1fr)

.info-item
  display: grid
  grid-template-columns: auto 1fr
  gap: 8px
  padding: 8px 12px
  background: #F5F5F5
  border-radius: 4px
  align-items: start

.coletas-list
  display: flex
  flex-direction: column
  gap: 2px

.info-label
  font-size: 12px
  font-weight: 600
  color: #0F5295
  text-transform: uppercase

.info-value
  font-size: 14px
  color: #707070

.info-value-column
  display: flex
  flex-direction: column

// === Estilos da expansão do histórico do posto avançado ===
/deep/ .historico-row-expanded
  td
    border-bottom: none !important

/deep/ .historico-expand-row
  background: transparent !important

  & > td,
  & > .q-td
    border-top: none !important
    border-bottom: none !important
    padding: 0 !important
    background: transparent !important

/deep/ .historico-expand-td
  border: none !important
  padding: 0 4px 8px 4px !important
  background: transparent !important

.historico-expand-loading
  display: flex
  align-items: center
  justify-content: center
  padding: 8px 16px
  color: #757575
  font-size: 13px
  background: #f2f2f2
  border-radius: 4px

.historico-expand-error
  padding: 8px 16px
  color: #c62828
  font-size: 13px
  background: #f2f2f2
  border-radius: 4px

  a
    color: #1565c0
    text-decoration: underline
    cursor: pointer
    font-weight: 500

.historico-expand-content
  display: flex
  flex-direction: column
  gap: 4px

.historico-expand-info-row
  display: flex
  gap: 8px
  align-items: stretch

.historico-expand-info-col
  flex: 1
  display: flex
  align-items: center
  background: #f2f2f2
  border-radius: 4px
  padding: 8px 12px

.historico-expand-info-label
  font-weight: 500
  color: #0f5295
  min-width: 100px
  margin-right: 8px
  font-size: 14px

.historico-expand-info-value
  color: #424242
  flex: 1
  font-size: 14px
  word-break: break-word

.historico-expand-empty
  color: #999
  font-size: 12px
  font-style: italic
  background: #f2f2f2
  border-radius: 4px
  padding: 8px 16px
</style>
